import PropTypes from 'prop-types';
import './ScanCardBase.scss';

/**
 * ScanCardBase – shared layout for both Recommended (Canned) and My Scan cards.
 *
 * Renders a 3-row card:
 *   1. Title row  – bold scan name (with optional trailing element, e.g. pencil icon)
 *   2. Body       – caller-supplied middle content (description text or field chips)
 *   3. Footer     – [Details] link (with optional trailing element, e.g. timestamp)
 *
 * Variant-specific overlays (e.g. actions menu) can be passed via `overlay`.
 *
 * @param {Object}   props
 * @param {string}   props.title           – Scan name displayed in the title row
 * @param {React.ReactNode} [props.titleExtra] – Element rendered after the title text (e.g. pencil icon)
 * @param {React.ReactNode} props.children     – Middle content area
 * @param {string}   props.detailsLabel    – Text for the details link
 * @param {Function} [props.onDetailsClick] – Handler for the details link click
 * @param {React.ReactNode} [props.footerExtra] – Element rendered after the details link (e.g. timestamp)
 * @param {React.ReactNode} [props.overlay]     – Absolutely-positioned overlay (e.g. actions menu)
 * @param {string}   [props.className]     – Additional CSS class(es) for the outer card
 * @param {boolean}  [props.active]        – Whether the card is selected
 * @param {Function} [props.onClick]       – Click handler for the whole card
 */
const ScanCardBase = ({
  title,
  titleExtra,
  titleNode,
  children,
  detailsLabel,
  onDetailsClick,
  footerExtra,
  overlay,
  className = '',
  active,
  onClick
}) => {
  return (
    <div
      className={`scan-card-base ${className} ${active ? 'active' : ''}`}
      onClick={onClick}
    >
      {overlay}
      <div className="scan-card-base-content">
        <div className="scan-card-base-title-row">
          {titleNode ? (
            titleNode
          ) : (
            <>
              <span className="scan-card-base-title">{title}</span>
              {titleExtra}
            </>
          )}
        </div>
        <div className="scan-card-base-body">{children}</div>
        <div className="scan-card-base-footer">
          <button
            className="scan-card-base-details-link"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDetailsClick?.(e);
            }}
          >
            [{detailsLabel}]
          </button>
          {footerExtra}
        </div>
      </div>
    </div>
  );
};

ScanCardBase.propTypes = {
  title: PropTypes.string,
  titleExtra: PropTypes.node,
  titleNode: PropTypes.node,
  children: PropTypes.node,
  detailsLabel: PropTypes.string.isRequired,
  onDetailsClick: PropTypes.func,
  footerExtra: PropTypes.node,
  overlay: PropTypes.node,
  className: PropTypes.string,
  active: PropTypes.bool,
  onClick: PropTypes.func
};

export default ScanCardBase;
